import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, Plus, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api } from "../../lib/api";

const NONE = "__none__";
const NEW = "__new__";
const Step = ({ n, title, hint, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
    <div className="mb-3 flex items-center gap-2.5">
      <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-600 text-sm font-bold text-white">{n}</span>
      <div><p className="font-bold text-slate-900">{title}</p>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
    </div>
    {children}
  </section>
);

const PickOrCreate = ({ value, onChange, items, newVal, setNewVal, placeholder, newLabel, testid }) => (
  <div className="space-y-2">
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
      <SelectTrigger data-testid={`${testid}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{placeholder}</SelectItem>
        {items.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
        <SelectItem value={NEW}><span className="flex items-center gap-1 font-semibold text-blue-700"><Plus className="h-3.5 w-3.5" /> {newLabel}</span></SelectItem>
      </SelectContent>
    </Select>
    {value === NEW && <Input autoFocus value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder="اكتب الاسم الجديد" data-testid={`${testid}-new`} />}
  </div>
);

export const AddUserPanel = ({ ctx, reload, onDone }) => {
  const [f, setF] = useState({ name: "", phone: "", password: "", national_id: "" });
  const [dept, setDept] = useState("");
  const [newDept, setNewDept] = useState("");
  const [title, setTitle] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [role, setRole] = useState("user");
  const [branches, setBranches] = useState([]);
  const [selB, setSelB] = useState([]);
  useEffect(() => { api.branchNames().then((b) => setBranches((Array.isArray(b) ? b : b?.items || []).filter(Boolean))).catch(() => {}); }, []);
  const scope = ctx.roles.find((r) => r.id === role)?.scope;
  const needBranch = role !== "manager" && scope === "branches";
  const [busy, setBusy] = useState(false);
  const titles = useMemo(() => ctx.job_titles.filter((j) => !j.department_id || j.department_id === dept), [ctx.job_titles, dept]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async () => {
    if (!f.name.trim() || !f.phone.trim() || !/^\d{6}$/.test(f.password)) { toast.warning("أدخل الاسم والجوال ورمزاً سرياً من 6 أرقام"); return; }
    if (needBranch && !selB.length) { toast.error("اختر فرعاً واحداً على الأقل — بدونه ما يشوف المستخدم أي بيانات"); return; }
    if (scope === "department" && !dept) { toast.error("هذا الدور نطاقه «قسمه فقط» — اختر قسماً للمستخدم"); return; }
    setBusy(true);
    try {
      let depId = dept;
      if (dept === NEW) { if (!newDept.trim()) throw new Error("اكتب اسم القسم الجديد"); depId = (await api.orgAddDept({ name: newDept.trim(), color: "#2563eb" })).id; setDept(depId); setNewDept(""); reload(); }
      let titleId = title;
      if (title === NEW) { if (!newTitle.trim()) throw new Error("اكتب المسمى الجديد"); titleId = (await api.orgAddTitle({ name: newTitle.trim(), department_id: depId || "" })).id; setTitle(titleId); setNewTitle(""); reload(); }
      const u = await api.createPhoneUser({ name: f.name.trim(), phone: f.phone.trim(), password: f.password, national_id: f.national_id.trim() || undefined });
      const uid = u.user_id || u.email;
      if (depId || titleId) await api.orgPlaceUser(uid, { department_id: depId || "", job_title_id: titleId || "" });
      if (needBranch) await api.setUserBranchesByEmail(u.email, selB);
      if (role !== "user") await api.orgSetAccess(uid, { role_id: role, perms: null });
      toast.success(`تمت إضافة ${f.name.trim()} — يدخل برقم الجوال وكلمة المرور`);
      setF({ name: "", phone: "", password: "", national_id: "" }); setDept(""); setTitle(""); setNewDept(""); setNewTitle(""); setRole("user"); setSelB([]);
      reload(); onDone?.(uid);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "تعذّرت الإضافة"); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-2xl space-y-3" data-testid="add-user-panel">
      <Step n={1} title="بيانات المستخدم" hint="يدخل المستخدم برقم الجوال وكلمة المرور">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <Input placeholder="الاسم" value={f.name} onChange={set("name")} data-testid="add-user-name" />
          <Input placeholder="رقم الهوية / الإقامة (للربط بملف الموظف)" dir="ltr" inputMode="numeric" value={f.national_id} onChange={set("national_id")} data-testid="add-user-nid" />
          <Input placeholder="05xxxxxxxx" dir="ltr" inputMode="tel" autoComplete="off" name="new-user-phone" value={f.phone} onChange={set("phone")} data-testid="add-user-phone" />
          <Input placeholder="الرمز السري (6 أرقام)" pin name="new-user-password" value={f.password} onChange={set("password")} data-testid="add-user-password" />
        </div>
      </Step>
      <Step n={2} title="القسم" hint="اختر قسماً موجوداً أو أضف قسماً جديداً">
        <PickOrCreate value={dept} onChange={(v) => { setDept(v); setTitle(""); }} items={ctx.departments} newVal={newDept} setNewVal={setNewDept} placeholder="بدون قسم" newLabel="قسم جديد…" testid="add-user-dept" />
      </Step>
      <Step n={3} title="المسمى الوظيفي">
        <PickOrCreate value={title} onChange={setTitle} items={titles} newVal={newTitle} setNewVal={setNewTitle} placeholder="بدون مسمى" newLabel="مسمى جديد…" testid="add-user-title" />
      </Step>
      <Step n={4} title="الدور والصلاحيات" hint="يمكنك تخصيص صلاحياته بالتفصيل بعد الإضافة بالضغط على اسمه">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ctx.roles.map((r) => (
            <button key={r.id} type="button" onClick={() => setRole(r.id)} data-testid={`add-user-role-${r.id}`}
              className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-start text-sm transition-colors ${role === r.id ? "border-blue-600 bg-blue-50 font-bold text-blue-800" : "border-slate-200 hover:border-blue-300"}`}>
              <span>{r.name}{r.system ? "" : " ★"}</span>{role === r.id && <Check className="h-4 w-4" />}
            </button>))}
        </div>
      </Step>
      {needBranch && (
        <Step n={5} title="الفروع المسموحة" hint="اختر فرعاً واحداً على الأقل — بدونه ما يشوف المستخدم أي بيانات">
          {!branches.length ? <p className="text-xs text-slate-400">لا توجد فروع</p> : (
            <div className="flex flex-wrap gap-1.5">
              {branches.map((b) => (
                <button key={b} type="button" onClick={() => setSelB((p) => (p.includes(b) ? p.filter((x) => x !== b) : [...p, b]))} data-testid={`add-user-branch-${b}`}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${selB.includes(b) ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}>{b}</button>))}
            </div>
          )}
        </Step>
      )}
      <Button className="h-11 w-full gap-2 text-base" disabled={busy} onClick={submit} data-testid="add-user-submit">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-5 w-5" />} إضافة المستخدم
      </Button>
    </div>
  );
};
